import { useVerificationStatusQuery } from '@/src/queries/earnings/verification-status'
import {
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Text,
  Image,
  Box,
} from '@chakra-ui/react'

interface PointProps {
  msg: string
}

const Point = ({ msg }: PointProps) => {
  return (
    <Flex justifyContent={'flex-start'} alignItems={'flex-start'} gap={2}>
      <Image
        src="/rewards/breakdown/check-circle.svg"
        alt="Tick"
        boxSize={5}
        mt={0.25}
      />
      <Text
        mb={4}
        fontSize={14}
        fontWeight={500}
        opacity={0.7}
        color={'#3e3e3e'}
      >
        {msg}
      </Text>
    </Flex>
  )
}

export default function VerifyIDError({
  isOpen,
  onClose,
  variant,
  size,
  handleProceed,
}: {
  isOpen: boolean
  onClose: () => void
  variant: 'pan' | 'bank' | 'attempts-over' | undefined
  handleProceed?: () => void
  size?: string
}) {
  const verificationStatus = useVerificationStatusQuery()

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={size ? size : 'md'}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {variant == 'bank'
            ? 'Incorrect Bank Details'
            : variant == 'pan'
              ? 'Incorrect PAN Details'
              : 'Attempts Exhausted'}
        </ModalHeader>
        <ModalCloseButton mt={2} />
        <hr />
        <ModalBody mt={2}>
          <Text mb={2} fontSize={32} fontWeight={700} color={'lyk-red'}>
            Oops!
          </Text>

          {variant == 'bank' ? (
            <Text
              mb={4}
              fontSize={14}
              fontWeight={500}
              opacity={0.7}
              color={'#3e3e3e'}
            >
              It seems there&apos;s an issue with the bank account information
              you provided. Before you can proceed with redeeming your account
              balance, please ensure that you enter the correct bank account
              details.
            </Text>
          ) : variant == 'pan' ? (
            <Text
              mb={4}
              fontSize={14}
              fontWeight={500}
              opacity={0.7}
              color={'#3e3e3e'}
            >
              It seems there&apos;s an issue with the PAN card information you
              provided. Before you can proceed with redeeming your account
              balance, please ensure that you enter the correct PAN card
              details.
            </Text>
          ) : null}

          {(variant == 'bank' || variant == 'pan') &&
          verificationStatus.data?.attemptLeft != '1' ? (
            <Text
              mb={4}
              fontStyle={'italic'}
              fontSize={14}
              fontWeight={600}
              opacity={0.5}
              color={'#3e3e3e'}
            >
              Note: You have only {verificationStatus.data?.attemptLeft}{' '}
              attempts left to verify your account.
            </Text>
          ) : null}

          {(variant == 'bank' || variant == 'pan') &&
          verificationStatus.data?.attemptLeft == '1' ? (
            <>
              <Text
                mb={4}
                fontStyle={'italic'}
                fontSize={14}
                fontWeight={600}
                opacity={0.5}
                color={'#3e3e3e'}
              >
                Note: This is your last attempt for online verification.
              </Text>

              <Text
                mb={4}
                fontSize={14}
                fontWeight={600}
                opacity={0.7}
                color={'#3e3e3e'}
              >
                If you fail the online verification again, you will have to
                verify manually.
              </Text>
            </>
          ) : null}

          {variant == 'attempts-over' ? (
            <>
              <Text
                mb={4}
                fontSize={14}
                fontWeight={500}
                opacity={0.7}
                color={'#3e3e3e'}
              >
                It seems that your attempt to verify your account has failed,
                and you have exhausted your allowed attempts.
              </Text>

              <Text
                mb={4}
                fontSize={14}
                fontWeight={500}
                opacity={0.7}
                color={'#3e3e3e'}
              >
                To proceed with account verification, please follow these steps:
              </Text>

              <Box mb={4}>
                <Point msg="Prepare scanned copies or clear photos of the required documents for identity verification, such as AADHAAR card or PAN card, and a cancelled cheque for bank account verification." />
                <Point
                  msg={`Compose an email with the subject line: "Account Verification - [Your Username/Email]".`}
                />
                <Point
                  msg={
                    'Attach the scanned copies or photos of the required documents to the email.'
                  }
                />

                <Point msg={'Send the email to support@lykstage.com'} />
              </Box>

              <Text
                mb={4}
                fontSize={14}
                fontWeight={500}
                opacity={0.7}
                color={'#3e3e3e'}
              >
                Once we receive your email and verify the provided documents,
                we&apos;ll process your account verification manually.
                You&apos;ll be notified via email once the verification is
                completed.
              </Text>

              <Text
                mb={4}
                fontSize={14}
                fontWeight={500}
                opacity={0.7}
                color={'#3e3e3e'}
              >
                Thank you for your cooperation and understanding. If you have
                any questions or need further assistance, please don&apos;t
                hesitate to contact our support team.
              </Text>
            </>
          ) : null}
        </ModalBody>
        <hr />

        <ModalFooter>
          <Flex gap={4} mb={0} justifyContent={'flex-end'}>
            {(variant == 'bank' || variant == 'pan') && (
              <Button
                px={10}
                _hover={{ opacity: 0.7 }}
                size={['sm', 'sm', 'md']}
                fontWeight={500}
                onClick={onClose}
              >
                Cancel
              </Button>
            )}
            <Button
              px={10}
              _hover={{ opacity: 0.7 }}
              size={['sm', 'sm', 'md']}
              fontWeight={500}
              onClick={() => {
                variant == 'attempts-over'
                  ? onClose()
                  : handleProceed && handleProceed()
              }}
              bg={'lyk-green'}
              color={'white'}
            >
              {variant == 'attempts-over' ? 'Close' : 'Proceed'}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
